import { useMemo, useEffect, useRef, useCallback, memo } from 'react';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { gql } from '@/apollo/gql';
import { KanbanMain } from '@/components/layouts/kanban';
import { useQuery } from '@apollo/client';

import { WithError, withError } from '@/components/errorBoundary/errorBoundary';
import {
  TRAY_PORTAL_RIGHT,
  TRAY_PORTAL_LEFT,
  TOOLTIP_PORTAL
} from '@/constant/htmlIds';
import { deepGet, formatNumber } from '@/utils';

import { withUser } from '@/contexts';
import { FIRST } from '@/constant/pagination';
import WorkflowOverviewHeader from './workflowOverviewHeader';
import { WorkflowOverviewActions } from './workflowOverviewActions';
import WorkflowOverviewTable from './workflowOverviewTable';
import { withWorkflowOverviewContext } from './context.workflowOverview';
import { generatePayload } from './export';

import {
  getSortFromUserDisplayConfig,
  getGroupFromUserDisplayConfig,
  getFiltersFromUserDisplayConfig,
  findGroupTraceStatesByIndex,
  countTraces
} from './utils';
import { ROUTE_WORKFLOW_DASHBOARD } from '@/constant/routes';

type Props = WithError<{
  match: any;
  user: any;
  context: any;
}>;

export const WorkflowOverview = memo((props: Props) => {
  const {
    match,
    user,
    context: { userDisplayConfig },
    errorContext: { handleError }
  } = props;

  const {
    loading: workflowLoading,
    error,
    data,
    previousData,
    fetchMore,
    refetch
  } = useQuery(WORKFLOW_QUERY, {
    variables: {
      workflowId: match.params.id,
      first: FIRST,
      filter: getFiltersFromUserDisplayConfig(userDisplayConfig),
      orderBy: getSortFromUserDisplayConfig(userDisplayConfig),
      groupBy: getGroupFromUserDisplayConfig(userDisplayConfig)
    },
    fetchPolicy: 'cache-and-network'
  });

  const workflow = data?.workflowByRowId ?? previousData?.workflowByRowId;

  const fetchMorePromise = useRef<Promise<void> | null>(null);

  // Is loading if the user info or the workflow info is missing,
  // and the gql call is loading.
  // The goal here is to render the old data when we are refetching
  const { loading: userLoading, me } = user;

  const loading = (!workflow || !me) && (workflowLoading || userLoading);

  const selectedGroupIndex = deepGet(userDisplayConfig, 'groupBy.selected');
  // Raise 404 page when workflow not found.
  useEffect(() => {
    const { params } = match;

    // This check avoids rerendering the component after the erroBoundary has been triggered
    if ((!workflowLoading && !workflow) || error) {
      handleError('workflow', params.id, ROUTE_WORKFLOW_DASHBOARD, error);
    }
  }, [error, workflowLoading, workflow, match, handleError]);

  useEffect(() => {
    const { name } = workflow || {};
    if (name) document.title = `${name} - Overview - Trace`;
  }, [workflow]);

  const handleOnLoadMore = useCallback(() => {
    if (!workflow) return;

    // If a fetch more query is in progress, do not try to load
    if (fetchMorePromise.current) return;

    // If grouping is in effect and all groups are collapsed, do not try to load
    // TODO: Handle group pagination; right now, only traces within the expanded group are paginated

    const groupBy = getGroupFromUserDisplayConfig(userDisplayConfig);
    if (groupBy && selectedGroupIndex === undefined) return;

    const { rowId: workflowRowId, groupedTraceStates } = workflow;

    // fetchMore will only fetch more traces for one group
    // (if groupBy is null, all the additional traces will be available in one group with index 'null')
    const traceStates = findGroupTraceStatesByIndex(
      groupedTraceStates,
      selectedGroupIndex
    );

    const { endCursor } = traceStates.pageInfo;

    fetchMorePromise.current = fetchMore({
      query: WORKFLOW_QUERY,
      variables: {
        workflowId: workflowRowId,
        first: FIRST,
        cursor: endCursor,
        filter: getFiltersFromUserDisplayConfig(userDisplayConfig),
        orderBy: getSortFromUserDisplayConfig(userDisplayConfig),
        groupBy
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        // we dont have new data
        if (!previousResult.workflowByRowId || !fetchMoreResult.workflowByRowId)
          return previousResult;

        const previousGroups =
          previousResult.workflowByRowId.groupedTraceStates;
        const newGroups = fetchMoreResult.workflowByRowId.groupedTraceStates;
        const previousTraceStates = findGroupTraceStatesByIndex(
          previousGroups,
          selectedGroupIndex
        );
        const newTraces = findGroupTraceStatesByIndex(
          newGroups,
          selectedGroupIndex
        );

        const { pageInfo: previousPageInfo, nodes: previousTraceStateNodes } =
          previousTraceStates;
        const { pageInfo: newPageInfo, nodes: newTraceStateNodes } = newTraces;

        return {
          workflowByRowId: {
            ...previousResult.workflowByRowId,
            groupedTraceStates: {
              ...previousGroups,
              nodes: previousGroups?.nodes.map(group => {
                if (!group) return null;

                // If selectedGroupIndex is undefined and grouping is not enabled,
                // it should update all the groups (there's only 1 group in that case).
                // Otherwise, pagination should only update the currently selected group
                // (i.e. prevent updates in non-selected groups here).
                if (groupBy && group.index !== selectedGroupIndex) return group;

                return {
                  ...group,
                  traceStates: {
                    ...group.traceStates,
                    // By updating page info with the `cursor` here, we update `fetchMore` function
                    pageInfo: {
                      ...previousPageInfo,
                      ...newPageInfo
                    },
                    nodes: [
                      // Put the new trace states after the previous ones
                      ...previousTraceStateNodes,
                      ...newTraceStateNodes
                    ]
                  }
                };
              })
            }
          }
        };
      }
    }).then(() => {
      fetchMorePromise.current = null;
    });
  }, [workflow, userDisplayConfig]);

  const exportSetup = useMemo(() => {
    if (!workflow) return;
    const nbTraces = countTraces(workflow);
    const { rowId, name } = workflow;
    return {
      generateExportPayload: async tracePayloadFn =>
        generatePayload(rowId, name, userDisplayConfig, tracePayloadFn),
      getConfirmationSetup: () => ({
        title: 'Export **data**',
        content: `You’re about to export data from the **${name}** overview table. Note that this will include all active filters, sorting and display options.`,
        confirmBtnText: `Export ${formatNumber(nbTraces)} traces`
      })
    };
  }, [workflow, userDisplayConfig]);

  const main = useMemo(
    () => (
      <>
        <WorkflowOverviewTable
          workflow={workflow}
          handleOnLoadMore={handleOnLoadMore}
          refetchTraces={refetch}
          exportSetup={exportSetup}
        />
        <WorkflowOverviewActions
          workflow={workflow}
          selectedGroupIndex={selectedGroupIndex}
        />
      </>
    ),
    [workflow, handleOnLoadMore, refetch, selectedGroupIndex]
  );

  // If the workflow id doesn't exist,
  // we return null and let errorBoundary render the error page
  if (error || (!workflow && !loading)) {
    return null;
  }

  return (
    <>
      <div id={TOOLTIP_PORTAL} />
      <div
        id={TRAY_PORTAL_LEFT}
        className="fixed top-0 bottom-0 left-0 z-5 flex w-auto flex-row flex-nowrap"
      />
      <div
        id={TRAY_PORTAL_RIGHT}
        className="fixed top-0 right-0 bottom-0 z-5 flex w-auto flex-row-reverse flex-nowrap"
      />

      <WorkflowOverviewHeader workflow={workflow} isLoading={loading} />
      <KanbanMain loading={loading} data-testid="layout-kanban-main">
        {!loading && main}
      </KanbanMain>
    </>
  );
});

export const WORKFLOW_QUERY = gql(`
  query WorkflowQuery(
    $workflowId: BigInt!
    $first: Int
    $cursor: Cursor
    $orderBy: [TraceStatesOrderByPath!]
    $filter: TraceStateFilter
    $groupBy: String
  ) {
    workflowByRowId(rowId: $workflowId) {
      rowId
      name
      canUpdate
      isReader
      config {
        rowId
        overview
        initState
        initActions
        stateSchema
        actions
        transitions
      }
      # If $groupBy is null, all the traces will be available in one group with index 'null'
      groupedTraceStates(path: $groupBy) {
        nodes {
          index
          traceStates(
            first: $first
            after: $cursor
            filter: $filter
            orderBy: $orderBy
          ) {
            totalCount
            pageInfo {
              endCursor
              hasNextPage
            }
            nodes {
              rowId
              data
              meta
              trace {
                rowId
              }
            }
          }
        }
      }
      groups {
        nodes {
          rowId
          label
          canAct: canActQuick
          name
          avatar
        }
      }
    }
  }
`);

export default compose(
  withRouter,
  withUser,
  withError,
  withWorkflowOverviewContext
)(WorkflowOverview);
